import React, { useEffect, useState } from 'react';
import InvoiceLink from './InvoiceLink';
import { Car, MapPin, Star } from 'lucide-react';
import apiService from '../apiService';
import { ratingText } from './ratingText';

/**
 * Location de véhicules web — agences (fournisseurs vehicle_rental), flotte
 * d'une agence, réservation (avec/sans chauffeur, durée heure/jour, départ
 * programmé, adresse de prise en charge), mes locations + annulation.
 * Le serveur calcule les montants et gère les statuts — mêmes endpoints
 * que le natif.
 */
export function RentalAgencies({ nav, citySlug }) {
  const [list, setList] = useState(null);
  useEffect(() => {
    const qs = new URLSearchParams({ type: 'vehicle_rental', limit: 30 });
    if (citySlug) qs.set('city_slug', citySlug);
    apiService.get(`/delivery/suppliers?${qs}`).then(r => setList(r?.suppliers || [])).catch(() => setList([]));
  }, [citySlug]);

  if (list === null) return <div className="mk-center"><div className="mk-spin" /> Chargement…</div>;
  return (
    <div className="mk-container">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
        <button className="mk-btn ghost" onClick={() => nav({ page: 'rentals-mine' })}><Car size={16} /> Mes locations</button>
      </div>
      {!list.length && <div className="mk-center"><Car size={42} /> Aucune agence de location pour le moment.</div>}
      <div className="mk-section" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 16 }}>
        {list.map(s => (
          <button key={s.supplier_id} className="mk-card" onClick={() => nav({ page: 'rental-fleet', supplierId: s.supplier_id })}>
            <div className="mk-card-imgwrap">
              {s.logo ? <img className="mk-card-img" style={{ height: 130 }} src={s.logo_thumb || s.logo} alt="" loading="lazy" /> : <div className="mk-card-img mk-card-ph" style={{ height: 130 }}>🚗</div>}
            </div>
            <span className="mk-card-title">{s.business_name}</span>
            <span className="mk-meta"><Star size={11} fill="#FFB900" color="#FFB900" /> {ratingText(s.rating, s.rating_count)}{s.address ? <> · <MapPin size={11} /> {s.address}</> : null}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function RentalFleet({ supplierId, nav }) {
  const [vehicles, setVehicles] = useState(null);
  const [supplier, setSupplier] = useState(null);
  useEffect(() => {
    Promise.all([
      apiService.get(`/delivery/vehicles?supplier_id=${supplierId}&available_only=false`).catch(() => null),
      apiService.get(`/delivery/suppliers/${supplierId}`).catch(() => null),
    ]).then(([v, s]) => {
      setVehicles(v?.vehicles || []);
      if (s?.success) setSupplier(s.supplier);
    });
  }, [supplierId]);

  if (vehicles === null) return <div className="mk-center"><div className="mk-spin" /> Chargement…</div>;
  return (
    <div className="mk-container">
      <div className="mk-store-head">
        <div className="mk-store-info"><h2>{supplier?.business_name || 'Agence'}</h2></div>
      </div>
      {!vehicles.length && <div className="mk-center">Aucun véhicule publié.</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 16 }}>
        {vehicles.map(v => (
          <div key={v.vehicle_id} className="mk-card" style={{ cursor: 'default' }}>
            <div className="mk-card-imgwrap">
              {v.photo ? <img className="mk-card-img" style={{ height: 150 }} src={v.photo} alt="" loading="lazy" /> : <div className="mk-card-img mk-card-ph" style={{ height: 150 }}>🚗</div>}
              {v.is_available === false && <span className="mk-flag">Indisponible</span>}
            </div>
            <span className="mk-card-title">{v.brand} {v.model} {v.year || ''}</span>
            <span className="mk-meta">{v.seats ? `${v.seats} places · ` : ''}{v.transmission || ''}</span>
            <span className="mk-card-price">
              {(v.price_per_day || 0).toLocaleString('fr-FR')} FC/jour
              {v.price_per_hour ? <span style={{ fontWeight: 600, color: 'var(--mb-text-2)' }}> · {(v.price_per_hour).toLocaleString('fr-FR')} FC/h</span> : null}
            </span>
            <button className="mk-btn primary" disabled={v.is_available === false}
              onClick={() => nav({ page: 'rental-book', supplierId, vehicleId: v.vehicle_id, vehicle: v })}>
              Réserver
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function RentalBook({ supplierId, vehicleId, vehicle: preset, nav }) {
  const [vehicle, setVehicle] = useState(preset || null);
  const [withChauffeur, setWithChauffeur] = useState(true);
  const [durationType, setDurationType] = useState('day');
  const [count, setCount] = useState(1);
  const [when, setWhen] = useState('');
  const [pickupText, setPickupText] = useState('');
  const [pay, setPay] = useState('cash');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (vehicle) return;
    apiService.get(`/delivery/vehicles?supplier_id=${supplierId}&available_only=false`)
      .then(r => setVehicle((r?.vehicles || []).find(v => v.vehicle_id === vehicleId) || null))
      .catch(() => {});
  }, [supplierId, vehicleId, vehicle]);

  if (!vehicle) return <div className="mk-center"><div className="mk-spin" /> Chargement…</div>;

  const unit = durationType === 'day' ? (vehicle.price_per_day || 0) : (vehicle.price_per_hour || 0);
  const estimated = unit * count;

  const book = async () => {
    setError(null); setBusy(true);
    try {
      const payload = {
        vehicle_id: vehicleId, with_chauffeur: withChauffeur,
        duration_type: durationType, duration_count: count,
        payment_method: pay, client_note: note.trim(),
      };
      if (when) payload.scheduled_start = new Date(when).toISOString();
      if (pickupText.trim()) payload.pickup_location = { address: pickupText.trim() };
      const res = await apiService.post('/vehicle-rentals', payload);
      const rental = res?.rental || res?.data?.rental;
      if (!res?.success || !rental) throw new Error(res?.error || 'Réservation impossible');
      nav({ page: 'rentals-mine' });
    } catch (e) { setError(e?.error || e?.message || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <div className="mk-container">
      <div className="mk-two">
        <div className="mk-panel">
          <h3>🚗 {vehicle.brand} {vehicle.model}</h3>
          {vehicle.photo && <img src={vehicle.photo} alt="" style={{ width: '100%', borderRadius: 12, maxHeight: 240, objectFit: 'cover' }} />}
          <p className="mk-note" style={{ marginTop: 10 }}>
            {vehicle.seats ? `${vehicle.seats} places · ` : ''}{vehicle.transmission || ''}{vehicle.fuel_type ? ` · ${vehicle.fuel_type}` : ''}
          </p>
        </div>

        <div className="mk-panel">
          <h3>Réserver</h3>
          <div className="mk-pay">
            <button className={withChauffeur ? 'on' : ''} onClick={() => setWithChauffeur(true)}>🧑‍✈️ Avec chauffeur</button>
            <button className={!withChauffeur ? 'on' : ''} onClick={() => setWithChauffeur(false)}>🔑 Sans chauffeur</button>
          </div>
          <div className="mk-pay" style={{ marginTop: 8 }}>
            <button className={durationType === 'day' ? 'on' : ''} onClick={() => setDurationType('day')}>À la journée</button>
            {vehicle.price_per_hour ? <button className={durationType === 'hour' ? 'on' : ''} onClick={() => setDurationType('hour')}>À l'heure</button> : null}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '12px 0' }}>
            <span style={{ fontWeight: 700 }}>{durationType === 'day' ? 'Jours' : 'Heures'}</span>
            <div className="mk-qty">
              <button onClick={() => setCount(v => Math.max(1, v - 1))}>−</button><b>{count}</b>
              <button onClick={() => setCount(v => v + 1)}>+</button>
            </div>
          </div>
          <div className="mk-field"><label>Départ souhaité (sinon dès que possible)</label>
            <input type="datetime-local" value={when} onChange={e => setWhen(e.target.value)} /></div>
          <div className="mk-field"><label>Lieu de prise en charge (facultatif)</label>
            <input value={pickupText} onChange={e => setPickupText(e.target.value)} placeholder="Adresse ou repère" /></div>
          <div className="mk-pay">
            <button className={pay === 'cash' ? 'on' : ''} onClick={() => setPay('cash')}>💵 Espèces</button>
            <button className={pay === 'balance' ? 'on' : ''} onClick={() => setPay('balance')}>👛 Solde</button>
          </div>
          <div className="mk-field" style={{ marginTop: 10 }}><label>Note (facultatif)</label>
            <input value={note} onChange={e => setNote(e.target.value)} /></div>
          <div className="mk-total grand"><span>Estimation</span><span>{estimated.toLocaleString('fr-FR')} FC</span></div>
          <p className="mk-note">L'agence confirme la demande ; le montant final est fixé par le serveur.</p>
          {error && <p className="mk-note err" style={{ marginTop: 8 }}>{error}</p>}
          <button className="mk-btn primary block" style={{ marginTop: 12 }} disabled={busy} onClick={book}>
            {busy ? 'Envoi…' : 'Demander la location'}
          </button>
        </div>
      </div>
    </div>
  );
}

const RSTATUS = {
  pending: ['En attente de l\'agence', '#FFB900'], accepted: ['Acceptée', '#10B981'], active: ['En cours', '#FF7800'],
  completed: ['Terminée', '#5C636E'], rejected: ['Refusée', '#DC2626'], cancelled: ['Annulée', '#DC2626'],
};

export function RentalsMine({ nav, currentUser }) {
  const [list, setList] = useState(null);
  const load = () => {
    const qs = new URLSearchParams({ client_id: currentUser.user_id, limit: 30 });
    apiService.get(`/vehicle-rentals?${qs}`).then(r => setList(r?.rentals || [])).catch(() => setList([]));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const cancel = async (id) => {
    if (!window.confirm('Annuler cette demande de location ?')) return;
    const r = await apiService.post(`/vehicle-rentals/${id}/cancel`, {}).catch(e => e);
    if (!r?.success) alert(r?.error || r?.message || 'Annulation impossible');
    load();
  };

  if (list === null) return <div className="mk-center"><div className="mk-spin" /> Chargement…</div>;
  return (
    <div className="mk-container">
      <div className="mk-panel" style={{ marginTop: 16 }}>
        <h3>🚗 Mes locations</h3>
        {!list.length && <div className="mk-center">Aucune location. <button className="mk-btn primary" onClick={() => nav({ page: 'rentals' })}>Voir les agences</button></div>}
        {list.map(r => {
          const [lbl, color] = RSTATUS[r.status] || [r.status, '#5C636E'];
          return (
            <div className="mk-line" key={r.rental_id}>
              <div className="info">
                <div className="name">{r.vehicle?.brand} {r.vehicle?.model} · {r.supplier?.business_name || ''}</div>
                <div className="opt">{r.duration_count} {r.duration_type === 'day' ? 'jour(s)' : 'heure(s)'} · {r.with_chauffeur ? 'avec chauffeur' : 'sans chauffeur'} · {(r.total_amount || r.amount || 0).toLocaleString('fr-FR')} FC</div>
              </div>
              <span className="mk-open" style={{ background: `${color}1c`, color }}>{lbl}</span>
              <InvoiceLink type="rental" id={r.rental_id} compact />
              {['pending', 'accepted'].includes(r.status) && (
                <button className="mk-iconbtn" title="Annuler" onClick={() => cancel(r.rental_id)} style={{ color: 'var(--mb-danger)' }}>✕</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
